import { useState, useRef, useEffect, useCallback } from "react";
import { useDateRange } from "../../context/DateRangeContext";
import { useActiveTheme, useTheme } from "../../context/ThemeContext";
import { CalendarDays } from "lucide-react";
import { subDays, startOfMonth, subMonths, endOfMonth } from "date-fns";

export function DateFilter({ allowCustom = true }: { allowCustom?: boolean }) {
    const { preset, setPreset, setDateRange } = useDateRange();
    const { theme } = useTheme();
    const activeTheme = useActiveTheme();
    const isDark = theme === "dark" || theme === "orange";
    const semantic = activeTheme.semantic[isDark ? "dark" : "light"];

    const s = {
        accent: "#F04911",  // Always orange for selected state
        accentDark: activeTheme.colors.gradient.end,
        border: semantic.border,
        text: semantic.foreground,
        textSecondary: semantic.mutedForeground,
        textMuted: semantic.mutedForeground,
        bgCard: semantic.card,
        inputBg: semantic.card,
        shadow: activeTheme.cardStyle.shadow[isDark ? "dark" : "light"],
        fontFamily: activeTheme.fontFamily,
        dark: isDark,
    };

    const opts = ['Today', 'Yesterday', 'Last 7 Days', 'This Month', 'Last Month', 'Last 3 Months', 'Last 6 Months', 'All Time'];
    const [showCustom, setShowCustom] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setShowCustom(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelectPreset = useCallback((o: string) => {
        setPreset(o);
        setShowCustom(false);

        const now = new Date();
        now.setHours(0, 0, 0, 0);

        let startDate = new Date(2000, 0, 1);
        let endDate = new Date();
        endDate.setHours(23, 59, 59, 999);

        if (o === 'Today') {
            startDate = new Date(); startDate.setHours(0, 0, 0, 0);
            endDate = new Date(); endDate.setHours(23, 59, 59, 999);
        } else if (o === 'Yesterday') {
            startDate = subDays(now, 1);
            endDate = new Date(startDate);
            endDate.setHours(23, 59, 59, 999);
        } else if (o === 'Last 7 Days') {
            startDate = subDays(now, 6);
        } else if (o === 'This Month') {
            startDate = startOfMonth(now);
        } else if (o === 'Last Month') {
            startDate = startOfMonth(subMonths(now, 1));
            endDate = endOfMonth(subMonths(now, 1));
            endDate.setHours(23, 59, 59, 999);
        } else if (o === 'Last 3 Months') {
            startDate = startOfMonth(subMonths(now, 2));
        } else if (o === 'Last 6 Months') {
            startDate = startOfMonth(subMonths(now, 5));
        } else if (o === 'All Time') {
            startDate = new Date(2000, 0, 1);
        } else if (o === 'Custom') {
            if (customStart && customEnd) {
                startDate = new Date(customStart + "T00:00:00");
                endDate = new Date(customEnd + "T23:59:59");
            }
        }

        setDateRange({ startDate, endDate });
    }, [setPreset, setDateRange, customStart, customEnd]);

    const handleApplyCustom = () => {
        if (customStart && customEnd) {
            handleSelectPreset('Custom');
        }
    };

    return (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {opts.map(o => (
                <button key={o} onClick={() => handleSelectPreset(o)} style={{
                    padding: '6px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 600,
                    border: `1px solid ${preset === o ? s.accent : (isDark ? 'rgba(255,255,255,0.2)' : s.border)}`,
                    background: preset === o ? `${s.accent}18` : (isDark ? 'rgba(255,255,255,0.06)' : 'transparent'),
                    color: preset === o ? s.accent : (isDark ? 'rgba(255,255,255,0.7)' : s.textSecondary),
                    cursor: 'pointer', transition: 'all 0.2s', fontFamily: s.fontFamily,
                }}>
                    {o}
                </button>
            ))}
            {allowCustom && (
                <div style={{ position: 'relative' }} ref={ref}>
                    <button onClick={() => setShowCustom(!showCustom)} style={{
                        padding: '6px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 600,
                        border: `1px solid ${preset === 'Custom' ? s.accent : (isDark ? 'rgba(255,255,255,0.2)' : s.border)}`,
                        background: preset === 'Custom' ? `${s.accent}18` : (isDark ? 'rgba(255,255,255,0.06)' : 'transparent'),
                        color: preset === 'Custom' ? s.accent : (isDark ? 'rgba(255,255,255,0.7)' : s.textSecondary),
                        cursor: 'pointer', fontFamily: s.fontFamily,
                        display: 'flex', alignItems: 'center', gap: '5px',
                    }}>
                        <CalendarDays size={13} /> Custom
                    </button>
                    {showCustom && (
                        <div style={{ position: 'absolute', top: '110%', right: 0, background: s.bgCard, border: `1px solid ${s.border}`, borderRadius: '12px', padding: '14px', zIndex: 100, minWidth: '260px', boxShadow: s.shadow, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ color: s.textMuted, fontSize: '10px', display: 'block', marginBottom: '3px' }}>From</label>
                                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: '8px', border: `1px solid ${s.border}`, background: s.inputBg, color: s.text, fontSize: '12px', outline: 'none', fontFamily: s.fontFamily }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ color: s.textMuted, fontSize: '10px', display: 'block', marginBottom: '3px' }}>To</label>
                                    <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: '8px', border: `1px solid ${s.border}`, background: s.inputBg, color: s.text, fontSize: '12px', outline: 'none', fontFamily: s.fontFamily }} />
                                </div>
                            </div>
                            <button onClick={handleApplyCustom} style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: `linear-gradient(135deg, ${s.accent}, ${s.accentDark})`, color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: s.fontFamily }}>Apply</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
